import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../context/AuthContext';
import './LandingPage.css';
import {
  IconVortex,
  IconAnalytics,
  IconLayers,
  IconFishSwarm,
  IconWave,
  IconForecast,
  IconLinkedIn,
  IconMail,
  IconWhatsApp,
  IconCheck
} from '../../components/common/Icons';

function LandingPage() {
  const { t } = useTranslation();
  const { authStatus } = useAuth();
  const navigate = useNavigate();

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formMessage, setFormMessage] = useState('');
  const [formSent, setFormSent] = useState(false);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    const body = `${t('landing.contact_form_name')}: ${formName}\n${t('landing.contact_form_email')}: ${formEmail}\n\n${formMessage}`;
    window.location.href = `mailto:aljes2310@gmail.com?subject=${encodeURIComponent(formSubject)}&body=${encodeURIComponent(body)}`;
    setFormSent(true);
    setFormName('');
    setFormEmail('');
    setFormSubject('');
    setFormMessage('');
  };
  const platformPath = authStatus === 'authenticated' ? '/select-service' : '/login';

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        }
      });
    }, { threshold: 0.1 });

    const sections = document.querySelectorAll('.landing-section');
    sections.forEach(section => observer.observe(section));

    return () => sections.forEach(section => observer.unobserve(section));
  }, []);

  return (
    <div className="landing-page-body">
      <div className="snap-container">
        <main className="landing-container">
          {/* Hero Section */}
          <section className="hero-section landing-section">
            <h1>{t('landing.hero_title')}</h1>
            <h2>{t('landing.hero_subtitle')}</h2>
            <p>{t('landing.hero_description')}</p>
            <div className="hero-buttons">
              <Link to={platformPath} className="cta-button primary">
                {t('landing.cta_demo')}
              </Link>
              <button onClick={scrollToContact} className="cta-button">
                {t('landing.cta_request_demo')}
              </button>
              <a href="/dashboard_hub" target="_blank" rel="noopener noreferrer" className="cta-button">
                {t('landing.cta_dashboards')}
              </a>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="landing-section">
            <h2 className="section-heading">{t('landing.features_title')}</h2>
            <div className="features-grid">

              <div className="feature-card">
                <IconFishSwarm />
                <h3>{t('landing.feature_zones_title')}</h3>
                <p>{t('landing.feature_zones_desc')}</p>
              </div>

              <div className="feature-card">
                <IconAnalytics />
                <h3>{t('landing.feature_models_title')}</h3>
                <p>{t('landing.feature_models_desc')}</p>
              </div>

              <div className="feature-card">
                <IconLayers />
                <h3>{t('landing.feature_viewer_title')}</h3>
                <p>{t('landing.feature_viewer_desc')}</p>
              </div>

              <div className="feature-card">
                <IconForecast />
                <h3>{t('landing.feature_forecast_title')}</h3>
                <p>{t('landing.feature_forecast_desc')}</p>
              </div>

              <div className="feature-card">
                <IconVortex />
                <h3>{t('landing.feature_eddies_title')}</h3>
                <p>{t('landing.feature_eddies_desc')}</p>
              </div>

              <div className="feature-card">
                <IconWave />
                <h3>{t('landing.feature_waves_title')}</h3>
                <p>{t('landing.feature_waves_desc')}</p>
              </div>

            </div>
          </section>

          {/* Analytics Section */}
          <section id="analytics" className="landing-section analytics-section">
            <div className="analytics-grid">
              <div className="analytics-left">
                <h2 className="analytics-heading">{t('landing.analytics_badge')}</h2>
                <p className="analytics-text">{t('landing.analytics_text')}</p>
                <a href="/dashboard_hub" target="_blank" rel="noopener noreferrer" className="cta-button analytics-cta">
                  {t('landing.analytics_cta')} →
                </a>
              </div>
              <div className="analytics-right">
                <div className="analytics-card card-enso">
                  <div className="analytics-card-accent" />
                  <div className="analytics-card-content">
                    <span className="analytics-card-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/>
                      </svg>
                    </span>
                    <div>
                      <h3>{t('landing.analytics_enso_title')}</h3>
                      <p>{t('landing.analytics_enso_desc')}</p>
                    </div>
                  </div>
                </div>
                <div className="analytics-card card-estado">
                  <div className="analytics-card-accent" />
                  <div className="analytics-card-content">
                    <span className="analytics-card-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 12h2"/><path d="M20 12h2"/><path d="M12 2v2"/><path d="M12 20v2"/>
                        <path d="M4.93 4.93l1.41 1.41"/><path d="M17.66 17.66l1.41 1.41"/>
                        <path d="M4.93 19.07l1.41-1.41"/><path d="M17.66 6.34l1.41-1.41"/>
                        <circle cx="12" cy="12" r="4"/>
                      </svg>
                    </span>
                    <div>
                      <h3>{t('landing.analytics_estado_title')}</h3>
                      <p>{t('landing.analytics_estado_desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Showcase Section */}
          <section id="showcase" className="landing-section">
            <div className="showcase-container">
              <div className="showcase-text">
                <h3>{t('landing.showcase_label')}</h3>
                <h2>{t('landing.showcase_title')}</h2>
                <p>{t('landing.showcase_desc')}</p>
              </div>
              <div className="showcase-mockup">
                <img src="/capas_overlap.png" alt={t('landing.mockup_alt')} className="mockup-image" loading="lazy" />
              </div>
            </div>
          </section>

          {/* Vista Dual Section */}
          <section id="vista-dual" className="landing-section">
            <div className="split-view-section">
              <div className="split-view-content">
                <h2 className="section-heading" style={{textAlign: 'left', marginBottom: '20px'}}>{t('landing.splitview_title')}</h2>
                <p className="split-view-description">{t('landing.splitview_desc')}</p>
                <ul className="split-view-features">
                  <li>{t('landing.splitview_feat1')}</li>
                  <li>{t('landing.splitview_feat2')}</li>
                  <li>{t('landing.splitview_feat3')}</li>
                  <li>{t('landing.splitview_feat4')}</li>
                </ul>
                <Link to={platformPath} className="cta-button" style={{marginTop: '30px'}}>{t('landing.splitview_cta')}</Link>
              </div>
              <div className="split-view-image">
                <div className="image-placeholder">
                  <img src="/splitview-demo.png" alt={t('landing.splitview_alt')} loading="lazy" />
                  <div className="image-overlay">
                    <span>{t('landing.capture_tool')}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Planes Section */}
          <section id="planes" className="landing-section">
            <h2 className="section-heading">{t('landing.plans_title')}</h2>
            <p className="section-subtitle" style={{maxWidth: '600px', margin: '0 auto 50px auto'}}>
              {t('landing.plans_subtitle_demo')}
            </p>
            <div className="planes-grid">
              <div className="plane-card pro popular">
                <div className="popular-badge">{t('landing.plan_popular')}</div>
                <div className="plane-header">
                  <h3>{t('split_view.bod_intel_name')}</h3>
                </div>
                <div className="plane-body">
                  <ul className="plane-features">
                    <li><IconCheck /> {t('split_view.bod_intel_feat1')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat2')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat3')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat4')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat5')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat6')}</li>
                    <li><IconCheck /> {t('split_view.bod_intel_feat7')}</li>
                  </ul>
                </div>
                <div className="plane-footer">
                  <a href="https://wa.me/51929076660" className="cta-button" target="_blank" rel="noopener noreferrer">{t('split_view.bod_intel_cta')}</a>
                </div>
              </div>

              <div className="plane-card premium">
                <div className="plane-header">
                  <h3>{t('split_view.bod_forecast_name')}</h3>
                </div>
                <div className="plane-body">
                  <ul className="plane-features">
                    <li><IconCheck /> {t('split_view.bod_forecast_feat1')}</li>
                    <li><IconCheck /> {t('split_view.bod_forecast_feat2')}</li>
                    <li><IconCheck /> {t('split_view.bod_forecast_feat3')}</li>
                    <li><IconCheck /> {t('split_view.bod_forecast_feat4')}</li>
                  </ul>
                </div>
                <div className="plane-footer">
                  <a href="https://wa.me/51929076660" className="cta-button" target="_blank" rel="noopener noreferrer">{t('split_view.bod_forecast_cta')}</a>
                </div>
              </div>

              <div className="plane-card basic">
                <div className="plane-header">
                  <h3>{t('split_view.personalizado_name')}</h3>
                </div>
                <div className="plane-body">
                  <ul className="plane-features">
                    <li><IconCheck /> {t('split_view.personalizado_feat1')}</li>
                    <li><IconCheck /> {t('split_view.personalizado_feat2')}</li>
                    <li><IconCheck /> {t('split_view.personalizado_feat3')}</li>
                    <li><IconCheck /> {t('split_view.personalizado_feat4')}</li>
                  </ul>
                </div>
                <div className="plane-footer">
                  <a href="https://wa.me/51929076660" className="cta-button" target="_blank" rel="noopener noreferrer">{t('split_view.personalizado_cta')}</a>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="landing-section contact-section-horizontal">
            <div className="horizontal-contact-container">
              <div className="contact-benefits">
                <h2 className="section-heading" style={{textAlign: 'left', marginBottom: '30px'}}>{t('landing.contact_title')}</h2>
                <div className="benefit-item-horizontal">
                  <span className="benefit-icon">🎯</span>
                  <div>
                    <h4>{t('landing.contact_b1_title')}</h4>
                    <p>{t('landing.contact_b1_desc')}</p>
                  </div>
                </div>
                <div className="benefit-item-horizontal">
                  <span className="benefit-icon">⚡</span>
                  <div>
                    <h4>{t('landing.contact_b2_title')}</h4>
                    <p>{t('landing.contact_b2_desc')}</p>
                  </div>
                </div>
                <div className="benefit-item-horizontal">
                  <span className="benefit-icon">📊</span>
                  <div>
                    <h4>{t('landing.contact_b3_title')}</h4>
                    <p>{t('landing.contact_b3_desc')}</p>
                  </div>
                </div>
              </div>

              <div className="contact-actions">
                <h3 className="contact-actions-title">{t('landing.contact_cta')}</h3>
                <a href="https://wa.me/51929076660" target="_blank" rel="noopener noreferrer" className="contact-button whatsapp">
                  <IconWhatsApp />
                  <div>
                    <span className="button-label">{t('landing.whatsapp_label')}</span>
                    <span className="button-sub">+51 929 076 660</span>
                  </div>
                </a>
                <form className="contact-form" onSubmit={handleFormSubmit}>
                  {formSent && <div className="contact-form-sent">{t('landing.contact_form_sent')}</div>}
                  <input type="text" placeholder={t('landing.contact_form_name')} value={formName} onChange={e => setFormName(e.target.value)} required />
                  <input type="email" placeholder={t('landing.contact_form_email')} value={formEmail} onChange={e => setFormEmail(e.target.value)} required />
                  <input type="text" placeholder={t('landing.contact_form_subject')} value={formSubject} onChange={e => setFormSubject(e.target.value)} required />
                  <textarea placeholder={t('landing.contact_form_message')} value={formMessage} onChange={e => setFormMessage(e.target.value)} required rows={4} />
                  <button type="submit" className="contact-form-submit">{t('landing.contact_form_submit')}</button>
                </form>
              </div>
            </div>
          </section>
        </main>

        <footer className="site-footer">
          <div className="footer-content">
            <div className="footer-credit">
              <p>Bluenetfish, software desarrollado por Alfredo Alvarado en colaboracion con Albor Tecnologico S.A.C <a href="https://www.albortecnologico.com" target="_blank"> (www.albortecnologico.com)</a> </p>
            </div>
            <div className="footer-socials">
              <a href="https://mail.google.com/mail/?view=cm&to=aljes2310@gmail.com&su=Consulta&body=Hola%20quiero%20contactarte" target="_blank" rel="noopener noreferrer" aria-label="Enviar correo"><IconMail /></a>
              <a href="https://wa.me/51929076660" target="_blank" aria-label="WhatsApp"><IconWhatsApp /></a>
              <a href="https://www.linkedin.com/in/alfredo-alvarado-espinoza-31058422a" target="_blank" aria-label="LinkedIn"><IconLinkedIn /></a>
            </div>
            <div className="footer-copyright">
              {t('landing.footer_copy')}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
